import { useEffect, useRef } from "react";
import { useOrg } from "../contexts/OrgContext";
import { usePiano } from "../utils/usePiano";
import { useToast } from "../utils/Toast";
import { useFornitore, urlLegali } from "../utils/fornitore";
import { MODULI, PIANI_INFO, LISTINO, statoModulo } from "../utils/pianiNexiora";

// ═══════════════════════════════════════════════════════════════════════════
// ZONA MODULO — sola lettura dei moduli non inclusi nel piano
// ═══════════════════════════════════════════════════════════════════════════
// Avvolge una pagina (App.js) o il contenuto di una scheda della commessa (ProjectView).
// Se il modulo non è incluso nel piano attuale, o l'organizzazione è in sola lettura
// totale, i pulsanti che CREANO, MODIFICANO o ELIMINANO dati vengono resi inattivi
// (grigi, con spiegazione) e il loro clic viene fermato con un messaggio chiaro.
// Restano attivi i pulsanti per consultare: aprire, filtrare, cercare, stampare, esportare.
// I pulsanti di scrittura si riconoscono dal testo (o dal titolo, per quelli con sola
// icona): è un aiuto per l'utente. Il blocco vero resta nelle Regole Firestore, che
// rifiutano comunque qualsiasi scrittura non consentita dal piano.
// ═══════════════════════════════════════════════════════════════════════════

const SEMPRE_CONSULTAZIONE = /(stampa|anteprima|\bpdf\b|esporta|scarica|excel)/i;
const CONSULTAZIONE = /(stampa|anteprima|\bpdf\b|esporta|scarica|excel|filtr|cerca|vedi|visualizza|apri|mostra|nascondi|indietro|torna|espandi|comprimi|dettagl|storico|calendario|settimana|mese|oggi|zoom|^chiudi$|^annulla$|^✕$|^×$|^x$)/i;
const SCRITTURA = /(^\s*[+➕]|\bnuov[oa]\b|aggiungi|salva|\bcrea\b|elimina|cancella|modifica|registra|conferma|invia|approva|rifiuta|respingi|archivia|ripristina|importa|duplica|carica|genera|assegna|segna|completa|pubblica|firma|timbra|\bentrata\b|\buscita\b|\bcollega\b|scollega|prendo in carico|chiudi (ordine|commessa|noleggio|intervento|periodo|richiesta)|annulla (ordine|richiesta|noleggio)|\battiva\b|disattiva|sposta|rinomina|converti|✅ tutto regolare)/i;

function testoControllo(el) {
  return `${(el.innerText || el.textContent || "").trim()} ${el.getAttribute("title") || ""} ${el.getAttribute("aria-label") || ""}`.trim();
}
function eScrittura(el) {
  if (!el || el.dataset?.consultazione === "true") return false;
  const t = testoControllo(el);
  if (!t) return false;
  if (SEMPRE_CONSULTAZIONE.test(t)) return false;
  if (CONSULTAZIONE.test(t) && !SCRITTURA.test(t.replace(CONSULTAZIONE, ""))) return false;
  return SCRITTURA.test(t);
}

let stileInserito = false;
function inserisciStile() {
  if (stileInserito || typeof document === "undefined") return;
  const s = document.createElement("style");
  s.textContent = ".nx-sola-lettura{opacity:.45!important;cursor:not-allowed!important;filter:grayscale(.4);}";
  document.head.appendChild(s); stileInserito = true;
}

export default function ZonaModulo({ modulo, escludiBloccoTotale = false, mostraBanner = true, children }) {
  const { effectiveOrgId } = useOrg();
  const { stato, loading } = usePiano(effectiveOrgId);
  const { addToast } = useToast();
  const fornitore = useFornitore();
  const ref = useRef(null);

  const moduloBloccato = !!modulo && !!stato && statoModulo(modulo, stato) === "readonly";
  const bloccoTotale = !!stato?.bloccoTotale && !escludiBloccoTotale;
  const attiva = !loading && (moduloBloccato || bloccoTotale);
  const richiesto = modulo ? PIANI_INFO[MODULI[modulo]?.piano || "pro"].label : "Pro";
  const messaggio = bloccoTotale
    ? "Nexiora è in sola lettura: la prova è terminata. Per modificare attiva il piano Pro o Gold; i dati restano consultabili ed esportabili."
    : `Sola lettura: ${MODULI[modulo]?.label || "questo modulo"} fa parte del piano ${richiesto}. I dati restano consultabili.`;

  // Segna in grigio i pulsanti di scrittura, anche quelli che compaiono dopo (finestre, righe nuove)
  useEffect(() => {
    const radice = ref.current;
    if (!attiva || !radice) return;
    inserisciStile();
    const marca = () => {
      radice.querySelectorAll("button, [role='button'], input[type='submit']").forEach(el => {
        if (el.classList.contains("nx-sola-lettura")) return;
        if (eScrittura(el)) {
          el.classList.add("nx-sola-lettura");
          el.setAttribute("aria-disabled", "true");
          el.setAttribute("title", messaggio);
        }
      });
    };
    marca();
    const obs = new MutationObserver(marca);
    obs.observe(radice, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      radice.querySelectorAll(".nx-sola-lettura").forEach(el => { el.classList.remove("nx-sola-lettura"); el.removeAttribute("aria-disabled"); });
    };
  }, [attiva, messaggio]);

  const fermaScrittura = (e) => {
    if (!attiva) return;
    const el = e.target.closest?.("button, [role='button'], input[type='submit']");
    if (el && ref.current?.contains(el) && (el.classList.contains("nx-sola-lettura") || eScrittura(el))) {
      e.preventDefault(); e.stopPropagation();
      addToast(messaggio, "error", 6000);
    }
  };
  const fermaInvio = (e) => { if (attiva) { e.preventDefault(); e.stopPropagation(); addToast(messaggio, "error", 6000); } };

  return (
    <>
      {attiva && mostraBanner && moduloBloccato && (
        <div style={{ margin:"10px 16px 0", padding:"10px 14px", background:"#fef3c7", border:"1px solid #fde68a", borderRadius:10,
          display:"flex", alignItems:"center", gap:10, fontSize:12.5, color:"#92400e", lineHeight:1.5, flexShrink:0 }}>
          <span style={{ fontSize:16 }}>🔒</span>
          <span style={{ flex:1 }}>{MODULI[modulo]?.label}: modulo del piano {richiesto}. Con il piano attuale i dati restano consultabili, ma non si possono creare o modificare.</span>
          <a href={urlLegali(fornitore).prezzi} target="_blank" rel="noreferrer" data-consultazione="true"
            style={{ fontSize:11.5, fontWeight:700, color:"#92400e", border:"1px solid #fcd34d", padding:"4px 10px", borderRadius:6, background:"#fff", textDecoration:"none", whiteSpace:"nowrap" }}>
            Piani e prezzi →
          </a>
        </div>
      )}
      <div ref={ref} style={{ display:"contents" }} onClickCapture={fermaScrittura} onSubmitCapture={fermaInvio}>
        {children}
      </div>
    </>
  );
}
